import React, { useEffect } from 'react';
import {
  BottomSheet,
  Box,
  HStack,
  NewContentButton,
  Text,
} from '@design-system/mobile';
import { FormProvider } from 'react-hook-form';
import styled from 'styled-components';
import AppBarBottom from '@/components/AppBarBottom';
import BasicInfo from '@/components/consult/BasicInfo';
import ConsultAgree from '@/components/consult/ConsultAgree';
import Header from '@/components/Header';
import useQuickConsult from './hooks/useQuickConsult';
import { QuickConsultInfoFromUntact } from '@remodeling/remodeling-manager';
import LoadImage from '@/components/LoadImage';
import { useDataLayer } from '@/providers/DataLayerProvider';
import {
  CONSULT_AGREE_LIST,
  GTM_CSTT,
  GTM_CST_KEYWORD,
  GTM_CTA_KEY_NAME,
  GTM_CTA_REMO_EVENT_NAME,
  GTM_QUICK_CONSULT_KEYWORD,
} from '@/constants';
import { getAsset } from '@/utils';
import { HEADER_HEIGHT } from '@/styles';
import TrackingService from '@/services/tracking';
import { isEmpty } from 'lodash-es';

interface QuickConsultProps {
  channelType?: string | string[];
  channelId?: string | string[];
  agentEmployeeNo?: string | string[];
  channelUrl?: string | string[];
  untactInfo?: QuickConsultInfoFromUntact;
  handleCloseLayerPopUp?: (id: string) => void;
  returnUrl?: string | string[];
  shopCode?: string | string[] | null;
}

const QuickConsult = ({
  channelType,
  channelId,
  agentEmployeeNo,
  untactInfo,
  channelUrl,
  handleCloseLayerPopUp,
  returnUrl,
  shopCode,
}: QuickConsultProps) => {
  const {
    isOpenWorkableBottomSheet,
    handleOpenBottomSheet,
    handleCloseBottomSheet,
    handleSubmitRequest,
    hookForm,
    isActiveSubmitButton,
    userInfo,
    workAvailableAreas,
    isApp,
    handleBack,
  } = useQuickConsult({
    channelType,
    channelId,
    agentEmployeeNo,
    channelUrl,
    handleCloseLayerPopUp,
    returnUrl,
    shopCode,
  });

  const { handlePushAndRemove } = useDataLayer();

  function getAssetSrc() {
    return getAsset('/quick-consult/event_B.png');
  }

  useEffect(() => {
    const key = GTM_CTA_KEY_NAME;

    handlePushAndRemove({
      data: {
        event: GTM_CTA_REMO_EVENT_NAME,
        [key]: GTM_CSTT,
        [GTM_CST_KEYWORD]: GTM_QUICK_CONSULT_KEYWORD,
      },
      key,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <StyledWrapper {...TrackingService.tagGTM('quick_consult')}>
        {!isApp() ? (
          <Header
            title="인테리어 상담신청"
            left={[
              {
                type: 'BACK',
                handleClick: handleBack,
              },
            ]}
          />
        ) : (
          <Header
            title="인테리어 상담신청"
            right={[
              {
                type: 'CLOSE',
                handleClick: handleBack,
              },
            ]}
          />
        )}

        <Container className={handleCloseLayerPopUp && 'isLayerPopUp'}>
          <StyledBox pb={52} backgroundColor="pick_color.white">
            <Box width="100%" pl={20} pr={20}>
              <LoadImage src={getAssetSrc()} />
            </Box>
            <Box px={20}>
              <FormProvider {...hookForm}>
                <StyledForm>
                  <BasicInfo
                    userInfo={userInfo && userInfo}
                    untactInfo={untactInfo}
                    workAvailableAreas={workAvailableAreas || []}
                    handleOpenBottomSheet={handleOpenBottomSheet}
                  />
                  <ConsultAgree consultAgreeItems={CONSULT_AGREE_LIST} />

                  <AppBarBottom isShow={true}>
                    <Box px={16} width="100%">
                      <NewContentButton
                        size="lg"
                        emphasis="high"
                        onClick={hookForm.handleSubmit(handleSubmitRequest)}
                        width="100%"
                        disabled={!isActiveSubmitButton}
                        {...TrackingService.tagGTM(
                          'quick_consult_submit_button',
                        )}
                      >
                        상담 신청
                      </NewContentButton>
                    </Box>
                  </AppBarBottom>
                </StyledForm>
              </FormProvider>
            </Box>
          </StyledBox>
        </Container>
      </StyledWrapper>

      {/* RD 채널 인입시 노출 바텀시트 */}
      {!isEmpty(workAvailableAreas) && (
        <BottomSheet
          isOpen={isOpenWorkableBottomSheet}
          variant="title"
          height="auto"
          zIndex={9999}
          option={{ title: '상담 가능 지역' }}
          handleClose={handleCloseBottomSheet}
        >
          <Box maxHeight={446} pb={16} mx={16}>
            <BottomSheetHStack overflowX="hidden" pt={20} pb={28} mx={16}>
              {workAvailableAreas?.slice(0, 20).map((item) => {
                return (
                  <Box position="relative" key={`work-available-areas-${item}`}>
                    <Box
                      width="1px"
                      height={12}
                      backgroundColor="color_chip.gray.200"
                      position="absolute"
                      top="1.5px"
                      left="-11px"
                    ></Box>
                    <Text variant="label_xlg_r" color="color_chip.primary">
                      {item}
                    </Text>
                  </Box>
                );
              })}
            </BottomSheetHStack>
          </Box>
        </BottomSheet>
      )}
    </>
  );
};

const StyledWrapper = styled(Box)`
  width: 100%;
  height: calc(100% - ${HEADER_HEIGHT}px);
  z-index: 100;
  background-color: #fff;
`;

const StyledForm = styled.form`
  padding-top: 40px;
`;

const StyledBox = styled(Box)`
  min-height: calc(100vh - 119px);
`;

const Container = styled(Box)`
  &.isLayerPopUp {
    height: calc(100% - ${HEADER_HEIGHT}px);

    overflow-x: hidden;
    overflow-y: auto;
    overscroll-behavior: contain;
  }
`;

const BottomSheetHStack = styled(HStack)`
  flex-wrap: wrap;
  gap: 20px 21px;
`;

export default QuickConsult;
